/**
 * One retry after a CSRF rejection, for every request transport the admin uses.
 *
 * The XSRF-TOKEN cookie lives 3600s. After roughly an hour idle the first
 * state-changing request carries an empty or expired token and the server
 * answers 403 `{"type": "csrf_error"}`. That same 403 re-issues the cookie
 * (`CSRFProtectionMiddleware.dispatch` refreshes it on a rejection), so one
 * retry with the cookie as it stands after the response succeeds.
 *
 * Two transports reach the server: `window.fetch` (most pages) and axios
 * (which Inertia visits also use). Both go through `isCsrfRejection` and
 * `getCsrfToken`, so there is one policy; `installCsrfRetry` wires it into each.
 * The retry is bounded to ONE attempt per request: a second rejection is
 * returned to the caller untouched.
 */

import { getCsrfToken } from '@/utils/csrf'

const CSRF_HEADER = 'X-XSRF-TOKEN'
const CSRF_ERROR_TYPE = 'csrf_error'
const FORBIDDEN = 403

/** True when a parsed error body is the server's CSRF rejection. */
export function isCsrfRejection(status, body) {
  return status === FORBIDDEN && body?.type === CSRF_ERROR_TYPE
}

// Same origin only, for both transports: the retry attaches the admin XSRF
// token, which must never be sent to another origin. Unparseable means no.
function isSameOrigin(url) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

// A request that can be sent twice. A stream body or a Request object cannot.
function isReplayable(input, init) {
  if (typeof input !== 'string' && !(input instanceof URL)) return false
  if (!isSameOrigin(input)) return false
  const body = init?.body
  return (
    body === undefined ||
    body === null ||
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof URLSearchParams
  )
}

/** Wrap a fetch implementation so a CSRF-rejected request is retried once. */
export function withCsrfRetryFetch(baseFetch) {
  return async function fetchWithCsrfRetry(input, init) {
    const response = await baseFetch(input, init)
    if (response.status !== FORBIDDEN || !isReplayable(input, init)) return response

    const body = await response
      .clone()
      .json()
      .catch(() => null)
    if (!isCsrfRejection(response.status, body)) return response

    const token = getCsrfToken()
    if (!token) return response

    const headers = new Headers(init?.headers)
    headers.set(CSRF_HEADER, token)
    return baseFetch(input, { ...init, headers })
  }
}

/** Add an axios response interceptor that retries a CSRF rejection once. */
export function addCsrfRetryInterceptor(axiosInstance) {
  axiosInstance.interceptors.response.use(undefined, (error) => {
    const config = error?.config
    const response = error?.response
    if (!config || !response || config._csrfRetried) return Promise.reject(error)
    if (!isCsrfRejection(response.status, response.data)) return Promise.reject(error)
    if (!isSameOrigin(axiosInstance.getUri(config))) return Promise.reject(error)

    const token = getCsrfToken()
    if (!token) return Promise.reject(error)

    config._csrfRetried = true
    config.headers.set(CSRF_HEADER, token)
    return axiosInstance.request(config)
  })
}

/** Install both, once, from main.js. */
export function installCsrfRetry(axiosInstance, target = window) {
  target.fetch = withCsrfRetryFetch(target.fetch.bind(target))
  addCsrfRetryInterceptor(axiosInstance)
}
